<template>
  <div :class="['NavPill', { 'is-vertical': vertical }]">
    <div class="pill-row">
      <slot />
    </div>

    <span v-if="label" class="pill-label">{{ label }}</span>
  </div>
</template>

<script setup lang="ts">
export interface NavPillProps {
  /* One label centred under the whole pill, for a group that reads as a single
     destination. Pass this or the buttons' own labels, not both. */
  label?: string;
  /* Icons one under another, with no words. The bar follows the stack. */
  vertical?: boolean;
}

withDefaults(defineProps<NavPillProps>(), { label: undefined, vertical: false });
</script>

<style lang="scss" scoped>
/* The rounded bar behind a row of NavPillButtons. Sized by --nav-cell-size so a
   caller can shrink the whole group from one declaration on an ancestor rather
   than threading a prop through every button. */
.NavPill {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Matches NavPillButton's label gap, so a pill-level label and per-button
     labels both leave the pill exactly --screen-nav-label-offset tall below the
     band. Bottom-anchored siblings align against that either way. */
  gap: var(--nav-label-gap, 0.375rem);
  pointer-events: auto;

  /* The background covers the icon band only, so labels sit below the shape
     rather than inside it. */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: var(--nav-cell-size, 3rem);
    border-radius: var(--nav-cell-size, 3rem);
    border: 1px solid var(--color-white-10);
    background-color: var(--color-dark-5);
    backdrop-filter: blur(8px);
    z-index: 0;
  }
}

.pill-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: var(--nav-pill-gap, 0.25rem);
  /* Tighter than it looks like it should be: a duo and the trio sit side by side
     on a 375px viewport, and 1rem here overflows it. */
  padding: 0 0.5rem;
}

.NavPill.is-vertical {
  width: var(--nav-cell-size, 3rem);
  /* The lead disc is allowed to break the capsule. */
  overflow: visible;

  &::before {
    inset: 0;
    width: 100%;
    height: auto;
  }

  .pill-row {
    flex-direction: column;
    padding: 0.5rem 0;
  }
}

.pill-label {
  position: relative;
  z-index: 1;
  height: calc(var(--screen-nav-label-offset) - var(--nav-label-gap, 0.375rem));
  line-height: calc(var(--screen-nav-label-offset) - var(--nav-label-gap, 0.375rem));
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--color-white-50);
  white-space: nowrap;
}
</style>
